import type { CV2Result } from '@/models/aiScan';

// Which statements a CVv2 disease grades: its worksheet finding class, and a
// word the statement text must contain (the aortic valve carries both AS and
// AR, so the class alone can't tell them apart). Mirrors ingestion's
// CV2_DISEASE_TO_ATTRIBUTE. `valve` is the word that tells the valves apart
// when one input pools several of them.
const CV2_STATEMENTS: Record<
  string,
  { findingClass: string; word: string; valve: string }
> = {
  mitral_regurgitation: {
    findingClass: 'MITRAL_VALVE',
    word: 'regurgitation',
    valve: 'mitral',
  },
  tricuspid_regurgitation: {
    findingClass: 'TRICUSPID_VALVE',
    word: 'regurgitation',
    valve: 'tricuspid',
  },
  aortic_stenosis: {
    findingClass: 'AORTIC_VALVE',
    word: 'stenosis',
    valve: 'aortic',
  },
  aortic_regurgitation: {
    findingClass: 'AORTIC_VALVE',
    word: 'regurgitation',
    valve: 'aortic',
  },
};

/**
 * The clips the CVv2 severity behind a statement was made on, in the order
 * the backend sends them, without repeats. A combined "normal" statement
 * that names several conditions gets all of their clips. Empty when nothing
 * matches.
 *
 * Pass every class of a group that pools several classes into one input:
 * ingestion files all of its statements under the first one, so "Severe
 * mitral regurgitation." arrives as an AORTIC_VALVE statement. The statement
 * must then also name the valve.
 */
export function cv2ClipsForStatement(
  results: readonly CV2Result[] | undefined,
  findingClasses: string | readonly string[],
  text: string
): string[] {
  const classes =
    typeof findingClasses === 'string' ? [findingClasses] : findingClasses;
  const pooled = classes.length > 1;
  const lower = text.toLowerCase();
  const uuids = (results ?? [])
    .filter((result) => {
      const match = CV2_STATEMENTS[result.disease];
      return (
        !!match &&
        classes.includes(match.findingClass) &&
        lower.includes(match.word) &&
        (!pooled || lower.includes(match.valve))
      );
    })
    .flatMap((result) =>
      result.dicoms
        .filter((dicom) => dicom.role === 'SEVERITY')
        .map((dicom) => dicom.dicomUuid)
    );
  return [...new Set(uuids)];
}
